import { ErpProviderLogo } from '@euler/app/flows/erp/components/ErpProviderLogo';
import { useErpProviders } from '@euler/app/flows/erp/functions/useErpProviders';
import { showErpToast } from '@euler/app/flows/erp/utils/showErpToast';
import { Colors, StatusColors } from '@euler/components/Colors';
import { FontFamily } from '@euler/components/typography';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { ErpProviderInfo } from '@euler/model/erp-integrations';
import { useServiceFactory } from '@euler/services/factory';
import { usePersistFn } from '@euler/utils/hooks';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { memo, useCallback, useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

export const ErpProviderSelectionScreen = wrapNavigatorScreen(
  () => {
    return <Content />;
  },
  {
    title: '选择服务商',
  },
);

const Content = memo(() => {
  const navigation = useNavigation<any>();
  const { storeErpIntegrationService } = useServiceFactory();
  const { connectableProviders, getProviderName, loading } = useErpProviders();
  const [currentProviderId, setCurrentProviderId] = useState<string>();

  const onSelect = usePersistFn((provider: ErpProviderInfo) => {
    // A store may connect exactly one provider for now. Say so here rather
    // than letting the server reject the login after credentials are typed.
    if (currentProviderId && provider.providerId !== currentProviderId) {
      showErpToast(
        `当前门店已连接${getProviderName(
          currentProviderId,
        )}，如需更换请先解除该连接`,
        'error',
      );
      return;
    }
    navigation.navigate('_erpLogin', {
      providerId: provider.providerId,
      providerName: provider.name,
    });
  });

  // Only a connection with live integrations constrains the choice. A dormant
  // one is a leftover and must not lock the store to that provider.
  const loadCurrentProvider = useCallback(async () => {
    try {
      const { connections } =
        await storeErpIntegrationService.getErpConnections();
      setCurrentProviderId(
        connections.find(x => x.activeIntegrationCount > 0)?.erpProviderId,
      );
    } catch (e) {
      setCurrentProviderId(undefined);
    }
  }, [storeErpIntegrationService]);

  useFocusEffect(
    useCallback(() => {
      void loadCurrentProvider();
    }, [loadCurrentProvider]),
  );

  if (loading && connectableProviders.length === 0) {
    return (
      <View css="flex: 1; align-items: center; justify-content: center;">
        <ActivityIndicator size="large" color={Colors.Gray2} />
      </View>
    );
  }

  return (
    <ScrollView css="flex: 1; background-color: #f7f7f7; padding: 15px;">
      <Text
        css={`
          font-family: ${FontFamily.NotoSans.Medium};
          font-size: 14px;
          color: #666;
          margin-bottom: 14px;
        `}
      >
        请选择您要连接的服务商
      </Text>

      <View css="flex-direction: row; flex-wrap: wrap; justify-content: space-between;">
        {connectableProviders.map(p => {
          const selected = p.providerId === currentProviderId;
          return (
            <TouchableOpacity
              key={p.providerId}
              activeOpacity={0.7}
              style={{
                width: '48%',
                backgroundColor: '#fff',
                borderRadius: 16,
                paddingVertical: 24,
                paddingHorizontal: 14,
                marginBottom: 16,
                alignItems: 'flex-start',
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.08,
                shadowRadius: 8,
                elevation: 3,
                borderWidth: selected ? 1.5 : 0,
                borderColor: selected ? '#8cbcf0' : 'transparent',
              }}
              onPress={() => onSelect(p)}
            >
              <ErpProviderLogo provider={p} size={56} />
              <Text
                style={{
                  fontFamily: FontFamily.NotoSans.Medium,
                  fontSize: 16,
                  color: '#333',
                  marginTop: 12,
                  marginBottom: 6,
                }}
              >
                {p.name}
              </Text>
              <Text style={{ fontSize: 12, color: '#999', minHeight: 34 }}>
                {p.description ?? p.productName}
              </Text>
              <View
                style={{
                  marginTop: 14,
                  paddingHorizontal: 10,
                  paddingVertical: 5,
                  borderRadius: 999,
                  backgroundColor: selected ? '#edf6ff' : Colors.Gray5,
                }}
              >
                <Text
                  style={{
                    fontSize: 11,
                    color: selected ? StatusColors.Info : Colors.Gray2,
                    fontFamily: FontFamily.NotoSans.Medium,
                  }}
                >
                  {selected ? '已连接，点击继续' : '点击连接'}
                </Text>
              </View>
            </TouchableOpacity>
          );
        })}
      </View>
    </ScrollView>
  );
});
